import React, { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";
import LanguageSwitcher from "./LanguageSwitcher";
import AnnouncementBanner from "./AnnouncementBanner";

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: t("nav_home"), href: "#" },
    { name: "FUKKONI Lite 🛍️", href: "#lite" },
    { name: t("nav_features"), href: "#features" },
    { name: t("solutions_badge"), href: "#solutions" },
    { name: t("nav_pricing"), href: "#pricing" },
    { name: t("nav_contact"), href: "#contact" },
  ];

  return (
    <header className="fixed w-full z-50 top-0 left-0 max-w-full overflow-visible">
      <AnnouncementBanner />
      <nav
        className={`w-full transition-all duration-300 ${
          scrolled
            ? "bg-white/95 backdrop-blur-xl py-2.5 sm:py-3 shadow-enterprise border-b border-slate-200/60"
            : "bg-slate-900/80 backdrop-blur-md py-3 sm:py-4 border-b border-white/5"
        }`}
      >
      <div className="max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <a href="#" className="flex-shrink-0 flex items-center group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary rounded-xl flex items-center justify-center shadow-premium group-hover:scale-105 transition-transform duration-300">
                <span className="text-white font-black text-lg sm:text-xl">F</span>
              </div>
              <span className={`ml-2.5 text-xl sm:text-2xl font-black tracking-tighter transition-colors duration-300 ${scrolled ? "text-slate-900" : "text-white"}`}>
                FUKKONI
              </span>
            </a>
          </div>

          <div className="hidden lg:flex items-center space-x-10">
            <div className="flex items-center space-x-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className={`text-sm font-bold tracking-tight transition-all duration-300 hover:text-primary ${
                    scrolled ? "text-slate-600" : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.name}
                </a>
              ))}
            </div>

            <div className="flex items-center space-x-5 border-l border-slate-200/20 pl-10">
              <LanguageSwitcher />
              <a
                href="https://app.fukkoni.com/login"
                className={`px-7 py-3 rounded-2xl font-bold text-sm transition-all duration-300 transform active:scale-95 ${
                  scrolled
                    ? "bg-primary text-white hover:bg-primary-dark shadow-premium"
                    : "bg-white text-primary hover:bg-slate-50"
                }`}
              >
                {t("hero_button_getStarted")}
              </a>
            </div>
          </div>

          <div className="lg:hidden flex items-center space-x-2 sm:space-x-3">
            <LanguageSwitcher />
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-xl transition-all duration-200 ${
                scrolled ? "text-slate-900 hover:bg-slate-100" : "text-white hover:bg-white/10"
              }`}
              aria-label="Toggle navigation menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        className={`lg:hidden absolute top-full left-0 w-full transition-all duration-300 ease-in-out overflow-hidden shadow-2xl ${
          isOpen ? "max-h-[90vh] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
        }`}
      >
        <div className="mx-3 my-2 p-5 bg-white/98 backdrop-blur-2xl shadow-enterprise rounded-3xl border border-slate-200/80 space-y-2 max-h-[80vh] overflow-y-auto">
          {navLinks.map((link, idx) => (
            <a
              key={link.name}
              href={link.href}
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-between px-4 py-3 text-sm sm:text-base font-bold text-slate-700 hover:text-primary hover:bg-emerald-50/60 rounded-xl transition-all group"
            >
              <span>{link.name}</span>
              <ChevronRight className="h-4 w-4 text-slate-400 group-hover:text-primary transition-all -translate-x-1 group-hover:translate-x-0" />
            </a>
          ))}
          <div className="pt-3 mt-2 border-t border-slate-100 flex flex-col gap-2">
            <a
              href="https://app.fukkoni.com/register?plan=lite"
              className="block w-full text-center px-4 py-3.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 rounded-xl font-black text-sm shadow-md transition-all active:scale-98"
            >
              🚀 {t("banner_cta") || "Créer ma Boutique Gratuite"}
            </a>
            <a
              href="https://app.fukkoni.com/login"
              className="block w-full text-center px-4 py-3 bg-primary hover:bg-primary-dark text-white rounded-xl font-bold text-sm shadow-premium transition-all active:scale-98"
            >
              {t("hero_button_getStarted")}
            </a>
          </div>
        </div>
      </div>
    </nav>
    </header>
  );
};

export default Navbar;
